import { useCallback, useEffect, useMemo, useState } from 'react';
import type { HttpClient } from '../client/http';

/**
 * Dados do plano de assinatura do app — shape retornado pelo backend em
 * GET /api/public/apps/:appId/plan. Escopo por-app, não por-user: o plano
 * é o mesmo pra qualquer visitante, autenticado ou não. Por isso o hook
 * pode rodar nas telas pre-auth (Anchor, Offer) antes do AuthGate resolver.
 *
 * `paywallConfig` é o jsonb livre do banco (inclui `anchorPriceCents`, copy
 * customizada, etc.). Tipamos como `Record<string, unknown>` pro app consumidor
 * narrow-caster dos próprios campos — evita contrato rígido aqui, já que
 * cada creator pode colocar copy/flags específicos.
 */
export interface PlanInfo {
  priceCents: number;
  yearlyPriceCents: number | null;
  currency: string;
  trialDays: number;
  /**
   * Substrato uniforme (App Store model, 2026-04-24): só dois modos.
   * - `'free'`: app sem paywall, todos os users têm acesso.
   * - `'paid'`: paywall ativo, captura cartão/Pix em day 0 + trial de
   *   `trialDays` antes da primeira cobrança.
   *
   * Valores legacy do DB (`'trial'`, `'pay_first'`) são normalizados pra
   * `'paid'` em runtime via {@link normalizeMode} — TS narrowing é breaking,
   * mas comportamento é transparent compat.
   */
  mode: 'free' | 'paid';
  active: boolean;
  paywallConfig: Record<string, unknown>;
}

/**
 * Normaliza valores legacy de `mode` (`'trial'`, `'pay_first'`) pra `'paid'`.
 * Único valor que passa direto é `'free'`. Qualquer string desconhecida
 * vira `'paid'` (default seguro: paywall ativo > acesso aberto por engano).
 *
 * Aplicado on-read (response do backend) pra evitar quebrar apps com rows
 * antigas no DB enquanto a migração de schema do mode acontece em paralelo.
 */
export function normalizeMode(raw: string): 'free' | 'paid' {
  return raw === 'free' ? 'free' : 'paid';
}

export interface PlanState {
  data: PlanInfo | null;
  loading: boolean;
  /**
   * Igual ao `subscription.initialLoadComplete` (G68): flipa `true` uma vez
   * depois do primeiro fetch resolver (sucesso OU falha). Telas podem usar
   * pra evitar skeleton eterno quando backend está offline.
   */
  initialLoadComplete: boolean;
  refresh: () => Promise<void>;
}

interface CacheEnvelope {
  data: PlanInfo;
  savedAt: number;
}

const CACHE_PREFIX = 'hook:plan:';
const CACHE_TTL_MS = 60_000;

function cacheKey(appId: string): string {
  return `${CACHE_PREFIX}${appId}`;
}

function readCache(appId: string): PlanInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(appId));
    if (!raw) return null;
    const env = JSON.parse(raw) as CacheEnvelope;
    if (!env.savedAt || Date.now() - env.savedAt > CACHE_TTL_MS) return null;
    return env.data;
  } catch {
    return null;
  }
}

function writeCache(appId: string, data: PlanInfo | null): void {
  if (typeof window === 'undefined') return;
  try {
    const key = cacheKey(appId);
    if (data === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() } as CacheEnvelope));
  } catch {
    // quota/disabled — silent fail
  }
}

export function usePlanState(client: HttpClient, appId: string): PlanState {
  const [data, setData] = useState<PlanInfo | null>(() => readCache(appId));
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    setData(readCache(appId));
    setInitialLoadComplete(false);
  }, [appId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.request<PlanInfo & { mode: string }>(
        'GET',
        `/api/public/apps/${appId}/plan`,
      );
      const normalized: PlanInfo = { ...res, mode: normalizeMode(res.mode) };
      setData(normalized);
      writeCache(appId, normalized);
    } catch (err) {
      // Segue last-good-state: errors transitórios (network, 5xx) não zeram
      // o cache. 404 (app sem config ainda) também não — deixa last-good
      // ou null. Apps renderizam skeleton/fallback quando `data === null`.
      console.warn('sdk.plan.refresh_failed', { err });
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [client, appId]);

  useEffect(() => { void refresh(); }, [refresh]);

  return useMemo(
    () => ({ data, loading, initialLoadComplete, refresh }),
    [data, loading, initialLoadComplete, refresh],
  );
}
